import {
  Button,
  Input,
  InputAmount,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import { formatCurrency } from '@vrplatform/utils';
import { Box, Group, LoadingOverlay, Modal, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useReservationLineMappingsInputOptions } from '@vrplatform/data';
import { useReservationReclassificationMutation } from '../_hooks/useReservationReclassificationMutation';
import type { Reservation } from '../_queries/useLedgerReservationDetailDrawerQuery';
import { useTeam } from '@vrplatform/data';
import { ReservationPostingDateSelectInput } from './ReservationPostingDateSelectInput';
import { getPostingDatePlaceholder } from './ReservationPostingDateSelectInput.utils';

const trapSpacesForRequiredFields = (value: string) =>
  !!value.trim() || 'This field is required';

type ReclassifyReservationLine = {
  reservationId: string;
  sourceLineId: string;
  sourceLineType: string;
  sourceDescription: string;
  sourceCentTotal: number;
};

type FormInputs = {
  lineType: string | null;
  description: string;
  centTotal: number | undefined;
  postingAt: string | null;
};

type Props = {
  reservation: {
    id: string;
    bookingPlatform?: Reservation['bookingPlatform'];
    currency: Reservation['currency'];
    bookedAt?: Reservation['bookedAt'];
    checkIn?: Reservation['checkIn'];
    checkOut?: Reservation['checkOut'];
  };
  closeModal: () => void;
  reservationLine: ReclassifyReservationLine | null;
  zIndex?: number;
};

const getInitialFormValues = (
  reservationLine: ReclassifyReservationLine | null
): FormInputs => ({
  lineType: null,
  description: '',
  centTotal: reservationLine?.sourceCentTotal ?? 0,
  postingAt: null,
});

export const ReservationFinancialsReclassifyModal = ({
  reservation,
  closeModal: cl,
  reservationLine,
  zIndex,
}: Props) => {
  const opened = !!reservationLine;
  const [search, setSearch] = useState('');

  const methods = useForm<FormInputs>({
    defaultValues: getInitialFormValues(reservationLine),
  });

  const lineType = useWatch({
    control: methods.control,
    name: 'lineType',
  });
  const centTotal = useWatch({
    control: methods.control,
    name: 'centTotal',
  });
  const description = useWatch({
    control: methods.control,
    name: 'description',
  });

  const maxReclassifiableCentTotal = Math.abs(
    reservationLine?.sourceCentTotal ?? 0
  );

  const lineMappingsQuery = useReservationLineMappingsInputOptions({
    search,
    skip: !opened,
    refetchOnMount: false,
    reservation: {
      bookingPlatform: reservation.bookingPlatform,
    },
  });
  const lineTypeOptions = useMemo(() => {
    return (lineMappingsQuery.data ?? []).filter((lineTypeOption) => {
      return lineTypeOption.value !== reservationLine?.sourceLineType;
    });
  }, [lineMappingsQuery.data, reservationLine?.sourceLineType]);

  const [team] = useTeam();
  const defaultRevenueRecognition = team?.defaultRevenueRecognition;

  const postingDatePlaceholder = useMemo(() => {
    const selectedLineTypeMapping = lineType
      ? (lineMappingsQuery.originalData ?? []).find(
          (mapping) => mapping.type === lineType
        )
      : undefined;

    const selectedChannelMapping = reservation.bookingPlatform
      ? selectedLineTypeMapping?.channelMappings.find(
          (channelMapping) =>
            channelMapping.bookingChannel === reservation.bookingPlatform
        )
      : undefined;

    const revRec =
      selectedChannelMapping?.revenueRecognition ??
      selectedLineTypeMapping?.revenueRecognition ??
      defaultRevenueRecognition;

    return getPostingDatePlaceholder(revRec);
  }, [
    defaultRevenueRecognition,
    lineMappingsQuery.originalData,
    lineType,
    reservation.bookingPlatform,
  ]);

  useEffect(() => {
    methods.reset(getInitialFormValues(reservationLine));
  }, [methods, reservationLine]);

  const closeModal = () => {
    cl();
    methods.reset(getInitialFormValues(null));
    setSearch('');
  };

  const { submit } = useReservationReclassificationMutation(
    reservation.id,
    closeModal
  );

  const sourceCentTotal =
    (reservationLine?.sourceCentTotal ?? 0) - (centTotal ?? 0);

  const submitReclassification = methods.handleSubmit(async (data) => {
    const lineType = data.lineType;

    if (!reservationLine || !lineType) {
      methods.setError('lineType', {
        message: 'Please select a line type',
      });
      return;
    }

    const amount = Math.round(data.centTotal ?? 0);

    await submit({
      existing: {
        lineId: reservationLine.sourceLineId,
        centTotal: reservationLine.sourceCentTotal,
        description: reservationLine.sourceDescription,
        type: reservationLine.sourceLineType,
      },
      updated: {
        lineType,
        description: data.description.trim(),
        centTotal: amount,
        postingAt: data.postingAt ?? undefined,
      },
    });
  });

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title="Reclassify line"
      styles={{
        title: { fontWeight: 500 },
      }}
      zIndex={zIndex}
      size={480}
      centered
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
          position: 'relative',
        })}
      >
        <LoadingOverlay visible={lineMappingsQuery.isFetching} />

        <Text size="sm" c="gray.7">
          Move some or all of this charge to a different line type. Reservation
          total stays the same.
        </Text>

        <Controller
          control={methods.control}
          name="lineType"
          rules={{
            validate: (value) => (value ? true : 'Please select a line type'),
          }}
          render={({ field, fieldState }) => {
            const value = field.value
              ? (lineTypeOptions.find(
                  (option) => option.value === field.value
                ) ?? null)
              : null;

            return (
              <InputWrapper
                label="Line type"
                error={fieldState.error?.message}
                required
              >
                <InputSelect
                  value={value}
                  setValue={(value) => {
                    field.onChange(value?.value ?? null);
                    methods.setValue('description', value?.label ?? '', {
                      shouldValidate: true,
                    });
                  }}
                  type="single"
                  inputProps={{
                    error: !!fieldState.error,
                    placeholder: 'Select line type',
                    withClearButton: false,
                  }}
                  data={{
                    options: lineTypeOptions,
                    loading: lineMappingsQuery.isLoading,
                    error: lineMappingsQuery.error,
                    sort: null,
                    onSearchChange: setSearch,
                  }}
                />
              </InputWrapper>
            );
          }}
        />

        <Controller
          control={methods.control}
          name="description"
          rules={{
            required: 'Description is required',
            validate: trapSpacesForRequiredFields,
          }}
          render={({ field, fieldState }) => (
            <InputWrapper
              label="Description"
              error={fieldState.error?.message}
              required
            >
              <Input
                value={field.value}
                setValue={field.onChange}
                error={!!fieldState.error}
                placeholder="Describe the reclassified line"
              />
            </InputWrapper>
          )}
        />

        <Controller
          control={methods.control}
          name="centTotal"
          rules={{
            validate: (value) => {
              if (typeof value !== 'number') return 'Please enter a number';
              if (value === 0) return 'Please enter an amount other than 0';
              if (Math.abs(value) > maxReclassifiableCentTotal) {
                return `Amount cannot exceed ${formatCurrency(
                  maxReclassifiableCentTotal / 100,
                  reservation.currency
                )}`;
              }

              return true;
            },
          }}
          render={({ field, fieldState }) => (
            <InputWrapper
              label="Reclassified amount"
              error={fieldState.error?.message}
              description={`You can reclassify up to ${formatCurrency(
                maxReclassifiableCentTotal / 100,
                reservation.currency
              )}`}
              required
            >
              <InputAmount
                value={
                  field.value === undefined
                    ? ''
                    : Number(field.value || 0) / 100
                }
                setValue={(value) => field.onChange(Number(value || 0) * 100)}
                decimalScale={2}
                error={!!fieldState.error?.message}
                placeholder="0.00"
                leftSection={<Text c="gray.5">$</Text>}
              />
            </InputWrapper>
          )}
        />

        <ReservationPostingDateSelectInput
          control={methods.control}
          name="postingAt"
          placeholder={postingDatePlaceholder}
          reservationDates={{
            bookedAt: reservation.bookedAt,
            checkIn: reservation.checkIn,
            checkOut: reservation.checkOut,
          }}
        />

        <Box
          sx={(theme) => ({
            alignItems: 'flex-start',
            backgroundColor: theme.colors.blue[0],
            borderRadius: theme.radius.sm,
            display: 'flex',
            gap: theme.spacing.xs,
            padding: theme.spacing.sm,
          })}
        >
          <Icon icon="InfoIcon" size={16} color="#2563EB" />
          <Text size="sm" c="gray.7">
            Original line{' '}
            <Text component="span" fw={600} c="dark">
              {reservationLine?.sourceDescription}
            </Text>{' '}
            will be{' '}
            <Text component="span" fw={600} c="dark">
              {formatCurrency(sourceCentTotal / 100, reservation.currency)}
            </Text>
            <br />
            New line{' '}
            <Text component="span" fw={600} c="dark">
              {description?.trim() || lineType
                ? lineTypeOptions.find((option) => option.value === lineType)
                    ?.label
                : '-'}
            </Text>{' '}
            will be{' '}
            <Text component="span" fw={600} c="dark">
              {formatCurrency((centTotal ?? 0) / 100, reservation.currency)}
            </Text>
          </Text>
        </Box>

        <Group justify="right" mt="xs">
          <Button
            onClick={closeModal}
            disabled={methods.formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitReclassification}
            loading={methods.formState.isSubmitting}
          >
            Save reclassification
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
